import {
    DeliveryTypeCategory,
    OpenTabCartFragment,
    OpenTabCatalogQuery,
    OpenTabDetailFragment,
    OpenTabState,
    OpenTabSummaryFragment,
    OpenTabsQuery,
} from '@/features/graphql/generated/graphql'
import { SUPPORTED_ACCOUNT_PAYMENT_METHODS } from '@/features/openTabs/constants'
import {
    AccountOrder,
    AccountOrderItem,
    AccountPayment,
    AccountPriceBreakdown,
    CatalogCategory,
    CatalogProduct,
    CatalogSection,
    FirebaseOpenTabSummary,
    OpenTabAccount,
    OpenTabAccountSummary,
    SupportedAccountPaymentMethod,
} from '@/features/openTabs/types'
import {
    getProductVariantName,
    resolveCurrency,
    toAmount,
} from '@/features/openTabs/utils/backendValues'
import { toAccountHistory } from '@/features/openTabs/utils/history/backendTransforms'
import { formatPrice } from '@/utils/priceHelper'
import currencyJs from 'currency.js'

const toAccountOrderItem = (
    item: OpenTabDetailFragment['submissions'][number]['items'][number],
    showUnsettledItems: boolean,
    fallbackCurrency: string
): AccountOrderItem => {
    const quantity = showUnsettledItems ? item.unsettledQuantity : item.quantity
    const totalPrice = showUnsettledItems ? item.unsettledPrice : item.totalPrice
    const currency = resolveCurrency(fallbackCurrency, totalPrice, item.unitPrice)

    return {
        id: item.uuid,
        name: getProductVariantName(item.productVariant),
        quantity,
        settledQuantity: item.quantity - item.unsettledQuantity,
        baseUnitPrice: toAmount(item.price.amount),
        unitPrice: toAmount(item.unitPrice.amount),
        unitContainerPrice: item.addedProductVariants.reduce(
            (total, addition) =>
                total.add(
                    currencyJs(addition.containerPrice?.amount ?? 0).multiply(addition.quantity)
                ),
            currencyJs(item.containerPrice?.amount ?? 0)
        ).value,
        totalAmount: toAmount(totalPrice.amount),
        currency,
        modifiers: [
            ...item.addedProductVariants.map((addition) => ({
                id: addition.uuid,
                type: 'addition' as const,
                name: getProductVariantName(addition.productVariant),
                quantity: addition.quantity,
                unitPrice: toAmount(addition.price.amount),
            })),
            ...item.toppings.map((topping) => ({
                id: topping.uuid,
                type: 'addition' as const,
                name: topping.topping.name,
                quantity: topping.quantity,
                unitPrice: toAmount(topping.price.amount),
            })),
            ...item.removedRecipeIngredients.map((ingredient) => ({
                id: ingredient.uuid,
                type: 'removed-ingredient' as const,
                name: ingredient.name,
                quantity: 1,
                unitPrice: 0,
            })),
        ],
    }
}

export const toCartPriceBreakdown = (
    cart?: OpenTabCartFragment,
    attachAutomaticContainers = true
): AccountPriceBreakdown => {
    const items = cart?.items ?? []
    const productContainers = items.reduce(
        (total, item) =>
            total.add(attachAutomaticContainers ? (item.totalContainerPrice?.amount ?? 0) : 0),
        currencyJs(0)
    )

    return {
        productsAmount: items.reduce(
            (total, item) => total.add(item.totalProductPrice.amount),
            currencyJs(0)
        ).value,
        containerAmount: (cart?.containerItems ?? []).reduce(
            (total, item) => total.add(item.totalPrice.amount),
            productContainers
        ).value,
    }
}

const toOrders = (tab: OpenTabDetailFragment, fallbackCurrency: string): AccountOrder[] => {
    const showUnsettledItems = tab.state === OpenTabState.Open

    return [...tab.submissions]
        .sort(
            (left, right) =>
                Date.parse(left.createdAt) - Date.parse(right.createdAt) ||
                left.uuid.localeCompare(right.uuid)
        )
        .flatMap((submission, index) => {
            const items = submission.items
                .map((item) => toAccountOrderItem(item, showUnsettledItems, fallbackCurrency))
                .filter((item) => !showUnsettledItems || item.quantity > 0)

            if (showUnsettledItems && items.length === 0) return []

            const currency = resolveCurrency(fallbackCurrency, submission.totalPrice)
            const totalAmount = showUnsettledItems
                ? items.reduce((total, item) => total.add(item.totalAmount), currencyJs(0)).value
                : toAmount(submission.totalPrice.amount)

            return [
                {
                    id: submission.uuid,
                    number: index + 1,
                    createdAt: String(submission.createdAt),
                    totalAmount,
                    currency,
                    items,
                },
            ]
        })
}

const toPayments = (tab: OpenTabDetailFragment, fallbackCurrency: string): AccountPayment[] =>
    [...tab.settlements]
        .sort(
            (left, right) =>
                Date.parse(left.createdAt) - Date.parse(right.createdAt) ||
                left.uuid.localeCompare(right.uuid)
        )
        .map((settlement) => ({
            id: settlement.uuid,
            orderUuid: settlement.order.uuid,
            method: settlement.order.paymentType,
            amount: toAmount(settlement.order.totalPrice.amount),
            currency: resolveCurrency(fallbackCurrency, settlement.order.totalPrice),
            paidAt: String(settlement.createdAt),
        }))

export const toAccountSummary = (
    tab: OpenTabSummaryFragment,
    number: number
): OpenTabAccountSummary => {
    const currency = tab.totalPrice.currency
    const closed = tab.state === OpenTabState.Closed
    const totalPaidAmount = tab.settlements.reduce(
        (total, settlement) => total.add(settlement.order.totalPrice.amount),
        currencyJs(0)
    ).value
    const totalAmount = closed ? totalPaidAmount : toAmount(tab.totalPrice.amount)
    const unsettledAmount = toAmount(tab.unsettledPrice.amount)

    return {
        id: tab.uuid,
        number,
        name: tab.name,
        status: tab.state === OpenTabState.Open ? 'open' : 'closed',
        openedAt: String(tab.createdAt),
        closedAt: closed
            ? tab.settlements.reduce<string | undefined>(
                  (latest, settlement) =>
                      !latest || Date.parse(settlement.createdAt) > Date.parse(latest)
                          ? String(settlement.createdAt)
                          : latest,
                  undefined
              )
            : undefined,
        totalAmount,
        unsettledAmount: closed ? 0 : unsettledAmount,
        paymentCount: tab.settlements.length,
        currency,
    }
}

export const toAccountDetail = (tab: OpenTabDetailFragment, number: number): OpenTabAccount => {
    const summary = toAccountSummary(tab, number)
    const payments = toPayments(tab, summary.currency)

    return {
        ...summary,
        orders: toOrders(tab, summary.currency),
        payments,
        paymentCount: payments.length,
        history: toAccountHistory(tab, summary.currency),
    }
}

const toCatalogProduct = (
    product: OpenTabCatalogQuery['categories'][number]['products'][number]
): CatalogProduct => ({
    id: product.uuid,
    name: product.name,
    price: formatPrice(product.priceInfo.price.amount, product.priceInfo.price.currency),
    priceAmount: product.priceInfo.price.amount,
    currency: product.priceInfo.price.currency,
    soldOut: product.soldOut,
    variantQuantity: product.productVariants.length,
    ...(product.label && {
        label: { name: product.label.name, color: product.label.color ?? undefined },
    }),
})

export const toCatalog = (
    categories: OpenTabCatalogQuery['categories']
): { categories: CatalogCategory[]; sections: CatalogSection[] } => ({
    categories: categories.map((category) => ({ id: category.uuid, name: category.name })),
    sections: categories.flatMap((category) => {
        const ownSection: CatalogSection[] = category.products.length
            ? [
                  {
                      id: `${category.uuid}-products`,
                      categoryId: category.uuid,
                      name: category.name,
                      products: category.products.map(toCatalogProduct),
                  },
              ]
            : []
        const subcategorySections =
            category.subcategories?.map((subcategory) => ({
                id: subcategory.uuid,
                categoryId: category.uuid,
                name: subcategory.name,
                products: subcategory.products.map(toCatalogProduct),
            })) ?? []

        return [...ownSection, ...subcategorySections]
    }),
})

export const isRenderableOpenTab = (tab: { state: OpenTabState }): boolean =>
    tab.state !== OpenTabState.Cancelled

export const toPaymentMethods = (
    mappings: OpenTabsQuery['deliveryPaymentMappings']
): SupportedAccountPaymentMethod[] => {
    const paymentTypes = mappings.find(
        ({ deliveryTypeCategory }) => deliveryTypeCategory === DeliveryTypeCategory.EatIn
    )?.paymentTypes

    return SUPPORTED_ACCOUNT_PAYMENT_METHODS.filter((method) => paymentTypes?.includes(method))
}

export const toFirebaseAccountSummary = (
    tab: FirebaseOpenTabSummary,
    number: number,
    licenceCurrency: string
): OpenTabAccountSummary => {
    const closed = tab.state === 'closed'
    const firstOrderTotalPrice = closed ? tab.settlements[0]?.orderTotalPrice : undefined
    const latestSettlement = closed
        ? tab.settlements.reduce<string | undefined>(
              (latest, settlement) =>
                  !latest || Date.parse(settlement.createdAt) > Date.parse(latest)
                      ? settlement.createdAt
                      : latest,
              undefined
          )
        : undefined

    return {
        id: tab.uuid,
        number,
        name: tab.name,
        status: closed ? 'closed' : 'open',
        openedAt: tab.createdAt,
        closedAt: latestSettlement,
        totalAmount: firstOrderTotalPrice
            ? tab.settlements.reduce(
                  (total, settlement) => total.add(settlement.orderTotalPrice!.amount),
                  currencyJs(0)
              ).value
            : toAmount(tab.totalPrice?.amount),
        unsettledAmount: closed ? 0 : toAmount(tab.unsettledPrice?.amount),
        paymentCount: tab.settlements.length,
        currency: tab.totalPrice?.currency ?? licenceCurrency,
    }
}
